// PARTS-SERVING-1 (2026-10-06): the part-details body of the diagram hotspot
// popup (DiagramInfoCardV2), drawn between the description and the `option`
// line (which stays LAST, ZAL10-OPTION-FIELD-1):
//
//   1. one block per `parts` group — `label: value` lines in array order; the
//      block's top margin is the small gap between groups
//   2. the `details` lines, same shape (`not_drawn` = the label alone)
//   3. the `links` line — primary first, then the others:
//      `Pozri list 31 · 34, 35`
//
// Input is already validated by `parseHotspotExtras`
// (customer/midas/utils/diagramHotspotParts.ts). Every bot string is a React
// text node — never innerHTML / dangerouslySetInnerHTML / markdown
// (SEC-REQ-5); pinned by __tests__/DiagramInfoCardPartsV2.test.tsx.
//
// Links are buttons ONLY when `onOpenLink` is given (the pinned, interactive
// card). The hover card is pointer-events:none and follows the cursor, so its
// links are plain text there; a click on the hotspot pins the card.

import React from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  HotspotExtras,
  HotspotInfoLine,
  HotspotSheetLink,
} from "../../utils/diagramHotspotParts";

interface Props {
  extras: HotspotExtras;
  /** Opens `pageId` in the overlay viewer. Absent = links are plain text. */
  onOpenLink?: (pageId: number) => void;
}

const InfoLine: React.FC<{ line: HotspotInfoLine }> = ({ line }) => (
  <div data-hotspot-line={line.key}>
    {line.value ? (
      <>
        <span className="text-midas-muted-stone">{line.label}:</span> {line.value}
      </>
    ) : (
      line.label
    )}
  </div>
);

const LINK_BUTTON_STYLE: React.CSSProperties = {
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
  background: "none",
  border: 0,
  padding: 0,
  margin: 0,
  cursor: "pointer",
};

const SheetLink: React.FC<{
  link: HotspotSheetLink;
  onOpenLink?: (pageId: number) => void;
}> = ({ link, onOpenLink }) => {
  const { translate } = useTranslation("chatBot");
  if (!onOpenLink) {
    return <span data-hotspot-link-page={link.pageId}>{link.label}</span>;
  }
  return (
    <button
      type="button"
      data-hotspot-link-page={link.pageId}
      aria-label={translate("midas.artifact.diagram.openSheetLink", {
        label: link.label,
      }) as string}
      onClick={() => onOpenLink(link.pageId)}
      className="rounded-sm text-midas-terracotta underline underline-offset-2 hover:text-midas-graphite focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-midas-terracotta"
      style={LINK_BUTTON_STYLE}
    >
      {link.label}
    </button>
  );
};

const DiagramInfoCardExtrasV2: React.FC<Props> = ({ extras, onOpenLink }) => {
  const { parts, details, links } = extras;
  return (
    <>
      {parts.map((group, gi) => (
        <div key={`g${gi}`} className="mt-1" data-hotspot-parts-group="true">
          {group.map((line, li) => (
            <InfoLine key={`${gi}-${li}`} line={line} />
          ))}
        </div>
      ))}
      {details.length > 0 && (
        <div className="mt-1" data-hotspot-details="true">
          {details.map((line, li) => (
            <InfoLine key={`d${li}`} line={line} />
          ))}
        </div>
      )}
      {links && (
        <div className="mt-1" data-hotspot-links="true">
          <SheetLink link={links.primary} onOpenLink={onOpenLink} />
          {links.others.length > 0 && " · "}
          {links.others.map((link, i) => (
            <React.Fragment key={link.pageId}>
              {i > 0 && ", "}
              <SheetLink link={link} onOpenLink={onOpenLink} />
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
};

export default DiagramInfoCardExtrasV2;
